import React, { useEffect, useState } from 'react'
import Cookies from 'js-cookie';
import Sheet from '@mui/joy/Sheet';
import Button from '@mui/joy/Button';
import Typography from '@mui/joy/Typography';
import CssBaseline from '@mui/joy/CssBaseline';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import ToastMessage from '../../common/ToastMessage';
import Visibility from '@mui/icons-material/Visibility';
import InputAdornment from '@mui/material/InputAdornment';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { EndPoints } from '../../utils/apiEndpoints';
import { useUserStore } from '../../store/useUserStore';
import { FieldErrors } from '../../domain/helpers/Errors';
import { darkModeBackgroud } from '../../utils/constants';
import { validateSchema } from '../../common/ValidateSchema';
import { useDarkModeStore } from '../../store/useDarkModeStore';
import { useLanguageStore } from '../../store/useLanguageStore';
import { TokenPayload } from '../../domain/helpers/TokenPayload';
import { loginUser, resetPassword } from '../../services/LoginService';
import { expiredCokkieInDay, StorageItems } from '../../utils/storageItems';
import { UserResetPassword, resetPasswordSchema } from '../../domain/helpers/UserResetPassword';

type PasswordFieldType = 'OldPassword' | 'NewPassword' | 'ConfirmNewPassword';
interface ShowPasswordInterface { OldPassword: boolean, NewPassword: boolean, ConfirmNewPassword: boolean }

export const ResetPassword = () => {
    const navigate = useNavigate();
    const { setUser } = useUserStore();
    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();

    const [errors, setErrors] = useState<FieldErrors>({});
    const [showPassword, setShowPassword] = useState<ShowPasswordInterface>({
        OldPassword: false,
        NewPassword: false,
        ConfirmNewPassword: false
    });

    const [form, setForm] = useState<UserResetPassword>({
        Email: '',
        OldPassword: '',
        NewPassword: '',
        ConfirmNewPassword: ''
    });

    useEffect(() => {
        const getEmail = localStorage.getItem(StorageItems.UPDATEEMAIL);
        const oldPassword = localStorage.getItem(StorageItems.OLDPASSWORD);
        let upForm = { ...form };

        if (getEmail) upForm.Email = getEmail;
        if (oldPassword) upForm.OldPassword = oldPassword;

        setForm(upForm);
    }, []);

    const fieldTextCss = {
        color: darkMode ? 'white' : 'black',
        '& .MuiInputLabel-root': {
            color: darkMode ? 'white' : 'black',
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: darkMode ? 'white' : 'black',
            },
            '&:hover fieldset': {
                borderColor: darkMode ? '#90caf9' : '#1976d2',
            },
            '&.Mui-focused fieldset': {
                borderColor: darkMode ? '#90caf9' : '#1976d2',
            },
            '& input': {
                color: darkMode ? 'white' : 'black',
            },
        },
    };


    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const isValid = validateSchema(resetPasswordSchema, form);

        setErrors(isValid.errors);
        if (!isValid.result) return;

        try {
            await resetPassword(form);
            ToastMessage.Success(language['ResetPassword']);
            localStorage.setItem(StorageItems.EMAIL, form.Email);
            localStorage.setItem(StorageItems.PASSWORD, form.NewPassword);
            const login = {
                Email: form.Email,
                Password: form.NewPassword
            }
            const response = await loginUser(login);
            if (response && response.token && response.refreshToken) {
                Cookies.set(StorageItems.TOKEN, response.token, { expires: expiredCokkieInDay });
                Cookies.set(StorageItems.REFRESHTOKEN, response.refreshToken, { expires: expiredCokkieInDay });
                localStorage.setItem(StorageItems.EMAIL, login.Email);
                localStorage.setItem(StorageItems.PASSWORD, login.Password);
                let decodedToken = jwtDecode(response.token);
                setUser(decodedToken as TokenPayload);
                navigate(EndPoints.ANALYTICS);
            } else {
                navigate(EndPoints.LOGIN)
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value, id } = e.target;
        setForm({ ...form, [id]: value });
        delete errors?.[id];
    };

    const showEyeIcon = (i: 'OldPassword' | 'NewPassword' | 'ConfirmNewPassword' | 'Email') => {
        if (i === 'Email') return 'text';
        else return showPassword[i] ? 'text' : 'password'
    }

    const handleEyeVisibility = (field: string) => {
        setShowPassword(prev => ({ ...prev, [field]: !prev[field as PasswordFieldType] }));
    };

    const items: string[] = ['Email', 'OldPassword', 'NewPassword', 'ConfirmNewPassword'];

    return (
        <div className="d-flex align-items-center justify-content-center" style={{ height: '100vh' }}>
            <CssBaseline />
            <Sheet
                sx={{
                    py: 1,
                    px: 2,
                    gap: 0,
                    my: 10,
                    width: 400,
                    display: 'flex',
                    borderRadius: 'md',
                    flexDirection: 'column',
                    boxShadow: '8px 8px 30px rgba(0, 0, 0, 0.2)',
                    background: darkMode ? darkModeBackgroud : 'white'
                }}
            >
                <div>
                    <Typography
                        level="h4"
                        component="h1"
                        className={`text-center fw-bold fs-2 mt-2 mb-4 ${darkMode && 'text-white'}`}
                    >
                        {language['ChangePassword']}
                    </Typography>
                </div>
                <form onSubmit={handleSubmit} className='d-flex flex-column'>
                    {
                        items.map((item) =>
                            <TextField
                                id={item}
                                key={item}
                                sx={fieldTextCss}
                                value={form[item]}
                                variant="outlined"
                                className='mt-2 mb-2'
                                label={language[item]}
                                helperText={errors?.[item]}
                                error={Boolean(errors?.[item])}
                                onChange={(e) => handleChange(e)}
                                type={showEyeIcon(item as PasswordFieldType)}
                                InputLabelProps={{ style: { color: darkMode ? 'white' : 'black' } }}
                                autoComplete={item === 'NewPassword' || item === 'ConfirmNewPassword' ? 'new-password' : 'off'}
                                InputProps={item.includes('Password')
                                    ? {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label={`toggle ${item} visibility`}
                                                    onClick={() => handleEyeVisibility(item)}
                                                    edge="end"
                                                >
                                                    {showPassword[item as PasswordFieldType] ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }
                                    : item === 'Email' ? { readOnly: true } : undefined}
                            />
                        )
                    }
                    <div className='mt-2 mb-2 d-flex aling-items-center gap-2'>
                        <Button
                            className='w-50'
                            onClick={() => navigate(EndPoints.LOGIN)}
                            sx={{
                                fontSize: '16px',
                                background: '#EBEDF0',
                                color: 'rgb(133, 146, 163)',
                                '&:hover': { background: '#D1D5DB' }
                            }}>
                            {language['Cancel']}
                        </Button>
                        <Button
                            type='submit'
                            className='w-50'
                            sx={{ fontSize: '16px' }}
                        >
                            {language['Save']}
                        </Button>

                    </div>
                </form>

            </Sheet>
        </div>
    );
}
export default ResetPassword;